import { useCallback, useEffect, useMemo, useState } from "react";
import type { ProgressState } from "../types";

const STORAGE_KEY = "ai-star-adventure-progress-v1";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function dayDistance(from: string, to: string) {
  const a = new Date(`${from}T00:00:00`).getTime();
  const b = new Date(`${to}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000);
}

const initialState: ProgressState = {
  version: 1,
  completedQuests: [],
  completedGates: [],
  stars: 0,
  hearts: 5,
  streak: 1,
  lastVisit: todayKey(),
};

function loadProgress(): ProgressState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const stored = JSON.parse(raw) as ProgressState;
    if (stored.version !== 1) return initialState;
    const today = todayKey();
    const distance = dayDistance(stored.lastVisit, today);
    if (distance === 1) return { ...stored, streak: stored.streak + 1, hearts: 5, lastVisit: today };
    if (distance > 1) return { ...stored, streak: 1, hearts: 5, lastVisit: today };
    return stored;
  } catch {
    return initialState;
  }
}

export function useProgress() {
  const [progress, setProgress] = useState<ProgressState>(loadProgress);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [progress]);

  const completeQuest = useCallback((id: string) => {
    setProgress((current) => {
      if (current.completedQuests.includes(id)) return current;
      return {
        ...current,
        completedQuests: [...current.completedQuests, id],
        stars: current.stars + 10,
      };
    });
  }, []);

  const completeGate = useCallback((id: string) => {
    setProgress((current) => {
      if (current.completedGates.includes(id)) return current;
      return {
        ...current,
        completedGates: [...current.completedGates, id],
        stars: current.stars + 8,
      };
    });
  }, []);

  const loseHeart = useCallback(() => {
    setProgress((current) => ({ ...current, hearts: Math.max(0, current.hearts - 1) }));
  }, []);

  const refillHearts = useCallback(() => {
    setProgress((current) => ({ ...current, hearts: 5 }));
  }, []);

  const reset = useCallback(() => setProgress(initialState), []);

  return useMemo(
    () => ({ progress, completeQuest, completeGate, loseHeart, refillHearts, reset }),
    [progress, completeQuest, completeGate, loseHeart, refillHearts, reset],
  );
}
